import { clsx } from 'clsx';
import { Login } from '../../components/Login/Login';
import type { Order, SubmittedRequest } from '../../graphql/query/getOrder';
import { useWorkflow } from '../../workflow/hooks';
import s from './LoginLayout.module.css';
import { Box } from '@repo/ui/Box';
import { Flex } from '@repo/ui/Flex';
import { NarvarFooter } from '../../components/NarvarFooter/NarvarFooter';
import { BrandedHeader } from '../../components/BrandedHeader/BrandedHeader';
import { client } from '../../graphql/client';
import { safeSessionStorage } from '@repo/lib/storage';
import { STORAGE_KEYS } from '../../utils/constants';
import { useCallback } from 'react';
import { useRetailerSettings } from '../../hooks/useRetailerSettings';

export type LoginSubmitData = {
  loginContext: {
    email?: string;
    orderNumber?: string;
    zipCode?: string;
  };
  order: Order;
  submittedRequests: SubmittedRequest[];
};

export const LoginLayout = () => {
  const workflowState = useWorkflow();
  const { features, requestId, updateAuthStatus, setContextFromLogin } =
    workflowState;
  const isOrderless = features?.isOrderless;
  const orderTokens = features?.orderTokens;
  const assets = features?.assets;
  const desktopImage = assets?.loginBackgroundImageDesktop;
  const mobileImage = assets?.loginBackgroundImageMobile;
  const { isError, isLoading } = useRetailerSettings();

  const handleSignIn = useCallback(
    ({
      loginContext: { email, zipCode },
      order,
      submittedRequests,
    }: LoginSubmitData) => {
      const {
        authorization: { token },
        orderNumber,
      } = order;
      if (token) {
        client.setHeader('X-Authentication', token);
        safeSessionStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
        safeSessionStorage.setItem(STORAGE_KEYS.ORDER_NUMBER, orderNumber);
        updateAuthStatus('authenticated');
        setContextFromLogin({
          loginContext: {
            email,
            orderNumber,
            zipCode,
          },
          order,
          submittedRequests,
        });
      }
    },
    [setContextFromLogin, updateAuthStatus],
  );

  if (!orderTokens || orderTokens.length === 0 || requestId || isOrderless)
    return null;

  if (!desktopImage || !mobileImage) {
    return (
      <Box isContainer className={clsx(s.centeredWrapper)}>
        {!isError && !isLoading && <BrandedHeader />}
        <Flex
          className={clsx(s.main, s.imageless)}
          grow="1"
          justifyContent="center"
          alignItems="center"
        >
          <Login onSubmit={handleSignIn} fields={orderTokens} isCentered />
        </Flex>
        <div className={s.footer}>
          <NarvarFooter />
        </div>
      </Box>
    );
  }

  return (
    <div className={s.pageWrapper}>
      {!isError && !isLoading && <BrandedHeader />}
      <Box isContainer className={clsx(s.sidebarWrapper)}>
        <Flex
          className={clsx(s.main, s.topLeft)}
          direction="column"
          gap={['g-40', 'md:g-48']}
          justifyContent="space-between"
        >
          <div
            className={s.bgImageMobile}
            style={{
              backgroundImage: `url(${mobileImage})`,
            }}
          />
          <div className={s.loginForm}>
            <Login onSubmit={handleSignIn} fields={orderTokens} />
          </div>
          <div className={s.footer}>
            <NarvarFooter />
          </div>
        </Flex>
        <div className={s.bgImageDesktopContainer}>
          <div
            className={s.bgImageDesktop}
            style={{
              backgroundImage: `url(${desktopImage})`,
            }}
          />
        </div>
      </Box>
    </div>
  );
};
